import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { AdminCategory, DiligenceAdminStore } from 'stores/diligence/DiligenceAdminStore';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useRef } from 'react';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { AdminDetailsOutput } from 'dtos/diligence/AdminDetailsOutput';

const LinkText = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
}));

type AdminModalProps = {
  store: DiligenceAdminStore;
  adminDetails?: AdminDetailsOutput;
};

const AdminModal = observer(({ store, adminDetails }: AdminModalProps) => {
  const {
    closing,
    startClosing,
    handleClosed,
    saveModal,
    categories,
    customCategories,
    usingRecommended,
    toggleCategory,
    setCategoryTitle,
    setCategoryAsk,
    addCustomCategory,
    deleteCustomCategory,
    toggleRecommended,
    saveStatus,
    valid,
  } = store;
  const needsFocus = useRef(false);
  const leftCategories = categories.slice(0, Math.ceil(categories.length / 2));
  const rightCategories = categories.slice(Math.ceil(categories.length / 2));

  const handleAddCategoryClick = () => {
    addCustomCategory();
    needsFocus.current = true;
  };

  const latestCustomInputRef = useCallback((node: HTMLElement | null) => {
    if (!needsFocus.current) return;
    if (node !== null) {
      const firstInput = node.querySelector('input[type="text"]');
      if (firstInput) {
        node.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'center',
        });
        (firstInput as HTMLInputElement).focus();
        needsFocus.current = false;
      }
    }
  }, []);

  return (
    <Dialog
      TransitionProps={{ onExited: handleClosed }}
      open={!closing}
      onClose={startClosing}
      fullWidth
      maxWidth={'md'}
      data-testid="admin-modal"
    >
      <DialogTitle sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          Edit Required Diligence
          <IconButton
            aria-label="close"
            onClick={startClosing}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        {adminDetails && (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
            <Typography component="div" variant="body2" data-testid="job-number">
              {adminDetails.jobNumber}
            </Typography>
            <Typography component="div" variant="body2" data-testid="salesforce-link" sx={{ marginLeft: '16px' }}>
              <LinkText href={adminDetails.salesforceLink} target="_blank" rel="noreferrer">
                Open Salesforce
              </LinkText>
            </Typography>
            <Typography component="div" variant="body2" data-testid="dropbox-link" sx={{ marginLeft: '16px' }}>
              <LinkText href={adminDetails.dropboxLink} target="_blank" rel="noreferrer">
                Open Dropbox
              </LinkText>
            </Typography>
          </div>
        )}
      </DialogTitle>
      <DialogContent>
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0px 0px 0px' }}
        >
          <DialogContentText>Select the due diligence required for this job.</DialogContentText>
          <FormControlLabel
            sx={{ margin: 0 }}
            control={<Switch checked={usingRecommended} onChange={toggleRecommended} />}
            label="Recommended Job Categories"
          />
        </div>
        <Grid container columnSpacing={4} sx={{ marginTop: '22px' }}>
          <Grid item xs={12} sm={6}>
            {leftCategories.map((category) => (
              <StandardCategory
                key={category.key}
                category={category}
                onToggleCategory={toggleCategory}
                onChangeAsk={setCategoryAsk}
              />
            ))}
          </Grid>
          <Grid item xs={12} sm={6}>
            {rightCategories.map((category) => (
              <StandardCategory
                key={category.key}
                category={category}
                onToggleCategory={toggleCategory}
                onChangeAsk={setCategoryAsk}
              />
            ))}
          </Grid>
        </Grid>
        <Divider sx={{ margin: '32px 0px' }} />
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <Typography component="div" variant="subtitle2" sx={{ flex: 1 }}>
            <strong>Custom Fields</strong>
          </Typography>
          <Button variant="outlined" data-testid="category-add" onClick={handleAddCategoryClick}>
            Add Custom Diligence
          </Button>
        </div>
        {customCategories.length > 0 && (
          <Grid container rowSpacing={3} columnSpacing={4}>
            {customCategories.map((category, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                key={category.key}
                ref={index + 1 === customCategories.length ? latestCustomInputRef : undefined}
              >
                <CustomCategory
                  category={category}
                  onChangeTitle={setCategoryTitle}
                  onChangeAsk={setCategoryAsk}
                  onDelete={deleteCustomCategory}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <div style={{ flex: 1 }} />
        <Button data-testid="admin-cancel" variant="outlined" onClick={startClosing}>
          Cancel
        </Button>
        <Button
          data-testid="admin-save"
          variant="contained"
          disabled={saveStatus === 'loading' || !valid}
          onClick={saveModal}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
});

type StandardCategoryProps = {
  category: AdminCategory;
  onToggleCategory: (category: AdminCategory) => void;
  onChangeAsk: (category: AdminCategory, text: string) => void;
};

const StandardCategory = observer(({ category, onToggleCategory, onChangeAsk }: StandardCategoryProps) => (
  <React.Fragment>
    <div>
      <FormControlLabel
        label={category.title}
        control={<Checkbox size="small" checked={category.required} onChange={() => onToggleCategory(category)} />}
      />
    </div>
    {category.required && (
      <div style={{ margin: '4px 0px 8px 29px' }}>
        <TextField
          fullWidth
          size="small"
          value={category.ask}
          onChange={(event) => onChangeAsk(category, event.target.value)}
          variant="outlined"
          multiline
          label={`Additional Diligence Info (Optional)`}
          inputProps={{
            'data-testid': 'standard-category-ask',
          }}
        />
      </div>
    )}
  </React.Fragment>
));

type CustomCategoryProps = {
  category: AdminCategory;
  onChangeTitle: (category: AdminCategory, title: string) => void;
  onChangeAsk: (category: AdminCategory, text: string) => void;
  onDelete: (category: AdminCategory) => void;
};

const CustomCategory = observer(({ category, onChangeTitle, onChangeAsk, onDelete }: CustomCategoryProps) => (
  <React.Fragment>
    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
      <Checkbox sx={{ marginLeft: '-11px' }} size="small" checked disabled />
      <TextField
        fullWidth
        size="small"
        value={category.title}
        onChange={(event) => onChangeTitle(category, event.target.value)}
        variant="outlined"
        required
        error={!category.title}
        inputProps={{
          'data-testid': 'custom-category-title',
        }}
        label={`Due Diligence Name (Required)`}
        InputProps={{
          endAdornment: (
            <Tooltip title="Delete diligence">
              <IconButton
                aria-label="delete diligence"
                data-testid="category-delete"
                onClick={() => onDelete(category)}
                edge="end"
              >
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          ),
        }}
      />
    </div>
    <div style={{ margin: '12px 0px 0px 27px' }}>
      <TextField
        fullWidth
        size="small"
        value={category.ask}
        onChange={(event) => onChangeAsk(category, event.target.value)}
        variant="outlined"
        multiline
        data-testid="custom-category-ask"
        label={`Additional Diligence Info (Optional)`}
        inputProps={{
          'data-testid': 'custom-category-ask',
        }}
      />
    </div>
  </React.Fragment>
));

export { AdminModal };
